import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type ResumenUnidades, type Unidad } from "@/lib/api";
import {
  aPayloadUnidad,
  filtrosIniciales,
  formUnidad,
  queryUnidades,
  superficieTotal,
  validarUnidad,
  type ErroresUnidad,
  type FiltrosUnidades,
  type FormUnidad,
  type PayloadUnidad,
} from "@/lib/unidades";

const SIN_ERRORES: ErroresUnidad = {};

/** Lógica del listado de unidades (sin JSX). Admin carga y duplica; los demás roles sólo miran. */
export function useUnidadesTab() {
  const { fid, membresia } = useContextoApp();
  const navegar = useNavigate();
  const base = `/api/fideicomisos/${fid}/unidades`;
  const esAdmin = membresia.rol === "admin";

  const [filtros, setFiltros] = useState<FiltrosUnidades>(filtrosIniciales);
  const listado = useApiResource<Unidad[]>(`${base}${queryUnidades(filtros)}`);
  const resumen = useApiResource<ResumenUnidades>(`${base}/resumen`);

  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<FormUnidad>(() => formUnidad());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const accionGuardar = useAsyncAction((datos: PayloadUnidad) => api.post<Unidad>(base, datos));
  const errores = validarUnidad(form);

  // Precio sugerido → precio de lista, en todas las disponibles (el backend saltea reservadas, vendidas y fuera de venta).
  const [usarAbierto, setUsarAbierto] = useState(false);
  const [usadas, setUsadas] = useState<number | null>(null);
  const accionUsar = useAsyncAction(() => api.post<{ actualizadas: number }>(`${base}/precio-lista/usar-sugerido`, { unidad_ids: null }));
  const items = listado.data ?? [];
  const disponiblesConSugerido = items.filter((u) => u.precio_sugerido_usd !== null && u.estado_venta === "disponible").length;

  const confirmarUsar = async () => {
    const r = await accionUsar.run();
    if (r) {
      setUsarAbierto(false);
      setUsadas(r.actualizadas);
      listado.recargar();
      resumen.recargar();
    }
  };

  const abrir = (inicial: FormUnidad) => {
    setForm(inicial);
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    setAbierto(true);
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (Object.keys(errores).length > 0) return;
    if (await accionGuardar.run(aPayloadUnidad(form))) {
      // Se queda en el listado (cargar 40 unidades con Duplicar es más rápido sin saltar a la ficha).
      setAbierto(false);
      listado.recargar();
      resumen.recargar();
    }
  };

  return {
    esAdmin,
    items,
    haySugerido: items.some((u) => u.precio_sugerido_usd !== null),
    disponiblesConSugerido,
    usarAbierto,
    abrirUsar: () => {
      accionUsar.limpiarError();
      setUsadas(null);
      setUsarAbierto(true);
    },
    cerrarUsar: () => setUsarAbierto(false),
    confirmarUsar,
    usando: accionUsar.loading,
    errorUsar: accionUsar.error,
    usadas,
    /** Ante un error, `data` es la respuesta anterior: no se muestra (avisa `errorCarga`). */
    resumen: resumen.error ? null : (resumen.data ?? null),
    cargando: listado.loading && !listado.data,
    errorCarga: listado.error?.message ?? resumen.error?.message ?? null,
    reintentar: () => {
      if (listado.error) listado.recargar();
      if (resumen.error) resumen.recargar();
    },
    filtros,
    cambiarFiltro: <K extends keyof FiltrosUnidades>(campo: K, valor: FiltrosUnidades[K]) => setFiltros((f) => ({ ...f, [campo]: valor })),
    limpiarFiltros: () => setFiltros(filtrosIniciales()),
    hayFiltros: queryUnidades(filtros) !== "",
    abierto,
    cerrar: () => setAbierto(false),
    abrirNuevo: () => abrir(formUnidad()),
    duplicar: (u: Unidad) => abrir(formUnidad(u, { duplicar: true })),
    form,
    cambiar: <K extends keyof FormUnidad>(campo: K, valor: FormUnidad[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    total: superficieTotal(form),
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    verFicha: (id: number) => void navegar(`/unidades/${id}`),
    verPonderacion: () => void navegar("/ponderacion"),
  };
}
